import { __ } from '@wordpress/i18n';
import domReady from '@wordpress/dom-ready';
import {
	render,
	createRoot,
	useEffect,
	useState,
	useCallback,
	useRef,
} from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import ReactPaginate from 'react-paginate';

const App = () => {
	const [ posts, setPosts ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ totalPosts, setTotalPosts ] = useState( 0 );
	const [ categories, setCategories ] = useState( [] );
	const [ filteredCategories, setFilteredCategories ] = useState( [] );
	const myRefs = useRef( [] );
	const allBtn = useRef( null );
	const searchInput = useRef( null );

	const getPosts = ( endpoint, queryParams = null ) => {
		apiFetch( {
			path: addQueryArgs( endpoint, queryParams ),
			parse: false,
		} ).then( ( response ) => {
			Promise.all( [ response.headers, response.json() ] ).then(
				( [ headers, data ] ) => {
					setTotalPosts( headers?.get( 'X-WP-Total' ) );

					if ( data ) {
						setIsLoading( false );
						setPosts( data );
					}
				}
			);
		} );
	};

	const debounce = ( func, wait = 500 ) => {
		let timeout;

		return ( ...args ) => {
			clearTimeout( timeout );
			timeout = setTimeout( () => func( ...args ), wait );
		};
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleSearch = useCallback(
		debounce( ( searchTerm ) => {
			setIsLoading( true );
			getPosts( '/wp/v2/dco_theme/?per_page=12&_embed', {
				search: searchTerm,
			} );
		} ),
		[]
	);

	const handleSubmit = ( e ) => {
		e.preventDefault();
		handleSearch( searchInput.current.value );
	};

	const handleSelectedCategory = ( value, isChecked, i ) => {
		allBtn.current.checked = false;

		if ( isChecked ) {
			setFilteredCategories( [ ...filteredCategories, value ] );
			myRefs.current[ i ].checked = true;
		} else {
			const filteredCtg = filteredCategories.filter(
				( ctg ) => ctg !== value
			);
			setFilteredCategories( filteredCtg );
			myRefs.current[ i ].checked = false;
		}
	};

	const allCheckBoxHandler = () => {
		setFilteredCategories( [] );
		myRefs.current.forEach( ( item ) => {
			item.checked = false;
		} );
	};

	const handlePageClick = ( event ) => {
		getPosts(
			`wp/v2/dco_theme/?per_page=12&page=${ event.selected + 1 }&_embed`
		);
	};

	useEffect( () => {
		if ( filteredCategories.length > 0 ) {
			getPosts(
				`wp/v2/dco_theme/?per_page=12&dco_theme_cats=${ filteredCategories.join(
					','
				) }=&_embed`
			);
		} else {
			setIsLoading( true );
			getPosts( 'wp/v2/dco_theme/?per_page=12&_embed' );

			apiFetch( {
				path: addQueryArgs( 'wp/v2/dco_theme_cats' ),
			} ).then( ( response ) => {
				setCategories( response );
			} );
		}
	}, [ filteredCategories ] );

	return (
		<div className="wp-block-do-blocks-third-party-themes">
			<aside>
				<div className="search-widget">
					<form onSubmit={ handleSubmit }>
						<div className="dco-tp-form-search">
							<input
								type="search"
								name="search"
								placeholder={ __( 'Search…', 'do-blocks' ) }
								onKeyUp={ ( e ) =>
									handleSearch( e.target.value )
								}
								ref={ searchInput }
							/>
							<button type="submit" className="search-icon">
								<svg
									width="11"
									height="11"
									viewBox="0 0 11 11"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M7.81714 7.06308L10.2982 9.55439C10.5081 9.76516 10.5012 10.1012 10.283 10.3038C10.1804 10.3993 10.0451 10.4517 9.90257 10.4517C9.75196 10.4517 9.61142 10.394 9.50693 10.2892L7.00701 7.77897C6.29716 8.26059 5.4669 8.5143 4.59565 8.5143C2.27488 8.5143 0.386719 6.69135 0.386719 4.45075C0.386719 2.21015 2.27488 0.387207 4.59565 0.387207C6.91642 0.387207 8.80459 2.21015 8.80459 4.45075C8.80459 5.40975 8.45506 6.33094 7.81714 7.06308ZM7.70628 4.45076C7.70628 2.7946 6.31074 1.44727 4.59533 1.44727C2.87991 1.44727 1.48438 2.7946 1.48438 4.45076C1.48438 6.10691 2.87991 7.45424 4.59533 7.45424C6.31074 7.45424 7.70628 6.10691 7.70628 4.45076Z"
										fill="white"
									/>
								</svg>
							</button>
						</div>
					</form>
				</div>

				<div className="categories">
					<h3>{ __( 'Category', 'do-blocks' ) }</h3>

					<form>
						<div className="dco-tpt-form-group">
							<input
								type="checkbox"
								id="all"
								ref={ allBtn }
								onChange={ allCheckBoxHandler }
							/>
							<label htmlFor="all">
								{ __( 'All', 'do-blocks' ) }
							</label>
						</div>

						{ categories?.map( ( category, i ) => (
							<div
								className="dco-tpt-form-group"
								key={ category.id }
							>
								<input
									type="checkbox"
									id={ category.slug }
									value={ category.id }
									onChange={ ( e ) =>
										handleSelectedCategory(
											e.target.value,
											e.target.checked,
											i
										)
									}
									ref={ ( el ) =>
										( myRefs.current[ i ] = el )
									}
								/>
								<label htmlFor={ category.slug }>
									{ category.name }
								</label>
							</div>
						) ) }
					</form>
				</div>
			</aside>

			<div style={ { width: '100%' } }>
				<div className="dco-themes-wrapper">
					{ isLoading && (
						<p style={ { flexBasis: ' 100%' } }>Loading…</p>
					) }
					{ posts.length === 0 && ! isLoading
						? 'Nothing Found'
						: posts?.map( ( post ) => (
								<div className="dco-tp-themes" key={ post.id }>
									{ post.featured_media !== 0 && (
										<img
											src={
												post._embedded[
													'wp:featuredmedia'
												][ 0 ].source_url
											}
											alt={
												post._embedded[
													'wp:featuredmedia'
												][ 0 ].alt_text
											}
											className="dco-tp-themes-thumbnail"
											loading="lazy"
										/>
									) }

									<div className="dco-tp-themes-details">
										<h3>{ post?.title?.rendered }</h3>
										<div
											dangerouslySetInnerHTML={ {
												__html: post?.excerpt?.rendered,
											} }
										></div>
									</div>

									<div className="dco-tp-themes-cta">
										<a
											href={
												post?.content?.rendered !== ''
													? post?.link
													: post?.affiliate_url
											}
											className="link"
										>
											{ __( 'Know more', 'do-blocks' ) }
										</a>
										<a
											href={ post?.affiliate_url }
											className="btn"
											target="_blank"
											rel="nofollow noreferrer"
										>
											{ __( 'Buy Now', 'do-blocks' ) }
										</a>
									</div>
								</div>
						  ) ) }
				</div>

				{ totalPosts > 12 && (
					<ReactPaginate
						className="navigation"
						breakLabel="..."
						nextLabel=">"
						onPageChange={ handlePageClick }
						pageCount={ parseInt( totalPosts ) / 12 }
						previousLabel="<"
						renderOnZeroPageCount={ null }
					/>
				) }
			</div>
		</div>
	);
};

domReady( () => {
	const mountWrap = document.querySelector(
		'.wp-block-do-blocks-third-party-themes'
	);

	if ( createRoot ) {
		createRoot( mountWrap ).render( <App /> );
	} else {
		render( <App />, mountWrap );
	}
} );
